import { useEffect, useState } from "react";
import { Sessions } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { DesktopTower, DeviceMobile, XCircle, Clock, MapPin } from "@phosphor-icons/react";

function deviceIcon(device) {
  const d = (device || "").toLowerCase();
  if (d.includes("iphone") || d.includes("android") || d.includes("ipad")) return DeviceMobile;
  return DesktopTower;
}

function fmt(d) {
  if (!d) return "—";
  try { return new Date(d).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return d; }
}

export default function SessionsPage() {
  const [data, setData] = useState({ sessions: [], is_owner: false });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const r = await Sessions.list();
      setData(r);
    } catch (e) {
      toast.error("Failed to load sessions");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const revoke = async (s) => {
    if (s.is_current) {
      if (!confirm("Revoking your CURRENT session will sign you out. Continue?")) return;
    } else {
      if (!confirm(`Sign out ${s.user_name || "this device"} on ${s.device}?`)) return;
    }
    try {
      await Sessions.revoke(s.token_id);
      toast.success("Session revoked");
      if (s.is_current) window.location.href = "/login";
      else load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to revoke");
    }
  };

  return (
    <div data-testid="sessions-page">
      <PageHeader
        overline={data.is_owner ? "Security · Workspace-wide" : "Security · Your devices"}
        title="Signed-in Devices"
        description={data.is_owner
          ? "Every active session across the whole team. Revoke any device to sign that browser out immediately."
          : "Every browser or device where you're currently signed in. Revoke any you don't recognize."}
        testId="sessions-header"
      />

      <div className="p-10 space-y-3 max-w-4xl stagger-in">
        {loading && <div className="py-10 text-center text-sm text-neutral-500">Loading…</div>}
        {!loading && data.sessions.length === 0 && (
          <div className="text-center py-20 text-sm text-neutral-500">No active sessions.</div>
        )}
        {data.sessions.map(s => {
          const Icon = deviceIcon(s.device);
          return (
            <Card key={s.token_id} className={`p-5 rounded-md flex items-center gap-4 ${s.is_current ? "border-emerald-500 bg-emerald-50" : "border-neutral-200"}`} data-testid={`session-${s.token_id}`}>
              <div className="w-12 h-12 rounded-full grid place-items-center flex-shrink-0" style={{ background: s.user_color || "#0A0A0A", color: "white" }}>
                <Icon size={20} weight="duotone" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-medium">{s.device}</div>
                  {s.is_current && <Badge className="bg-emerald-600 text-white text-[10px] font-mono">THIS DEVICE</Badge>}
                  {data.is_owner && s.user_name && (
                    <Badge variant="outline" className="text-[10px] font-mono">
                      <span className="w-3 h-3 rounded-full mr-1 inline-block" style={{ background: s.user_color }} />
                      {s.user_name}
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-4 text-xs text-neutral-500 font-mono mt-2 flex-wrap">
                  <span className="flex items-center gap-1"><Clock size={11} /> last active {fmt(s.refreshed_at)}</span>
                  <span>· signed in {fmt(s.created_at)}</span>
                  {s.ip && <span className="flex items-center gap-1"><MapPin size={11} /> {s.ip}</span>}
                </div>
              </div>
              <Button size="sm" variant="outline" onClick={() => revoke(s)} data-testid={`revoke-${s.token_id}`}>
                <XCircle size={14} className="mr-2" /> Revoke
              </Button>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
